"use client";

import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import Image from "next/image";
import Link from "next/link";
import { Offer, ProductData } from "@/services/dataTypes";
import { discardHTMLTags, getBaseImageUrl } from "@/constants/hooks";
import OfferDuration from "./OfferDuration";

interface Props {
    data: Offer | ProductData;
    onClose: () => void;
    domain: string;
    merchantHref: string;
    finalDiscountTag?: string | null;
    merchantImg?: string | null;
    productImg?: string | null;
}

const SimpleOfferModal = ({
    data,
    onClose,
    domain,
    merchantHref,
    finalDiscountTag,
    merchantImg,
    productImg,
}: Props) => {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [copied, setCopied] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [showFullDesc, setShowFullDesc] = useState(false);
    const [codeRevealed, setCodeRevealed] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    /* ── Mount + animation in ── */
    useEffect(() => {
        setMounted(true);
        const t = requestAnimationFrame(() => setVisible(true));
        document.body.style.overflow = "hidden";
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") handleClose();
        };
        window.addEventListener("keydown", handleEsc);
        return () => {
            window.removeEventListener("keydown", handleEsc);
            document.body.style.overflow = "unset";
            cancelAnimationFrame(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Image resolution ── */
    useEffect(() => {
        if (productImg) {
            setImageSrc(getBaseImageUrl(domain, productImg, ""));
        } else if (
            (data as any)?.offer_type?.name === "product" &&
            (data as ProductData)?.product_image
        ) {
            setImageSrc(getBaseImageUrl(domain, (data as ProductData).product_image, ""));
        } else if (merchantImg) {
            setImageSrc(getBaseImageUrl(domain, merchantImg, ""));
        } else if ((data as Offer).merchant?.merchant_logo) {
            setImageSrc(getBaseImageUrl(domain, (data as Offer).merchant.merchant_logo, ""));
        }
    }, [data, domain, merchantImg, productImg]);

    /* ── Animated close ── */
    const handleClose = () => {
        setVisible(false);
        setTimeout(onClose, 280);
    };

    /* ── Copy logic ── */
    const handleCopy = () => {
        if (!data?.coupon_code) return;
        const done = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        };
        if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(data.coupon_code).then(done).catch(() => fallbackCopy(data.coupon_code!, done));
        } else {
            fallbackCopy(data.coupon_code, done);
        }
    };

    const fallbackCopy = (text: string, cb: () => void) => {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.cssText = "position:fixed;left:-9999px;top:0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
            document.execCommand("copy");
            cb();
        } catch {
            /* silent */
        }
        document.body.removeChild(ta);
    };

    const finalUrl = data?.url?.startsWith("/") ? data.url.replace(/^\/+/, "") : data?.url;
    const absoluteOutUrl = `/${finalUrl}`;
    const cleanDesc = discardHTMLTags(data?.offer_detail || "");
    const isLongDesc = cleanDesc.length > 160;
    const hasCoupon = !!data?.coupon_code;

    if (!mounted) return null;

    return ReactDOM.createPortal(
        /* ── Backdrop ── */
        <div
            className="fixed inset-0 z-[50000] flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{
                background: visible ? "rgba(8,28,17,0.82)" : "rgba(8,28,17,0)",
                backdropFilter: visible ? "blur(10px)" : "blur(0px)",
                transition: "background 0.28s ease, backdrop-filter 0.28s ease",
            }}
            onClick={handleClose}
        >
            {/* ── Modal shell ── */}
            <div
                ref={modalRef}
                className="relative flex flex-col overflow-hidden w-full"
                style={{
                    maxWidth: "420px",
                    background: "#081C11",
                    border: "1px solid rgba(122,252,32,0.18)",
                    clipPath:
                        "polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px))",
                    maxHeight: "90vh",
                    boxShadow:
                        "0 0 0 1px rgba(122,252,32,0.06), 0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(122,252,32,0.07)",
                    transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
                    opacity: visible ? 1 : 0,
                    transition: "transform 0.28s cubic-bezier(0.34,1.56,0.64,1), opacity 0.28s ease",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Invisible scrollbar — Chrome/Safari */}
                <style>{`.modal-scroll::-webkit-scrollbar{display:none}`}</style>
                {/* ── Dot-grid texture ── */}
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, rgba(122,252,32,0.06) 1px, transparent 0)",
                        backgroundSize: "22px 22px",
                        opacity: 1,
                    }}
                />

                {/* ── Top neon accent bar ── */}
                <div
                    className="absolute top-0 left-0 right-0 h-[2px] z-10"
                    style={{
                        background:
                            "linear-gradient(90deg, transparent 0%, #7AFC20 25%, #7AFC20 75%, transparent 100%)",
                    }}
                />

                {/* ── Corner L-brackets ── */}
                <div className="absolute top-0 left-0 w-6 h-[2px] z-20" style={{ background: "#7AFC20" }} />
                <div className="absolute top-0 left-0 w-[2px] h-6 z-20" style={{ background: "#7AFC20" }} />
                <div className="absolute bottom-0 right-0 w-6 h-[2px] z-20" style={{ background: "rgba(122,252,32,0.35)" }} />
                <div className="absolute bottom-0 right-0 w-[2px] h-6 z-20" style={{ background: "rgba(122,252,32,0.35)" }} />

                {/* ── Close button ── */}
                <button
                    onClick={handleClose}
                    aria-label="Close modal"
                    className="absolute top-3.5 right-3.5 z-30 w-8 h-8 flex items-center justify-center transition-all duration-200 hover:scale-110 focus:outline-none group/close"
                    style={{
                        background: "rgba(122,252,32,0.06)",
                        border: "1px solid rgba(122,252,32,0.18)",
                        color: "rgba(122,252,32,0.5)",
                        clipPath:
                            "polygon(0 0, calc(100% - 5px) 0, 100% 5px, 100% 100%, 5px 100%, 0 calc(100% - 5px))",
                    }}
                >
                    <svg
                        className="w-3 h-3 group-hover/close:rotate-90 transition-transform duration-200"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* ── Scrollable body ── */}
                <div
                  className="overflow-y-auto flex-grow relative z-10 modal-scroll"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >

                    {/* ── Hero: image + discount badge ── */}
                    <div
                        className="relative flex flex-col items-center pt-8 pb-6 px-6"
                        style={{ borderBottom: "1px solid rgba(122,252,32,0.07)" }}
                    >
                        {/* Glow behind image */}
                        <div
                            className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-28 rounded-full pointer-events-none"
                            style={{
                                background: "radial-gradient(circle, rgba(122,252,32,0.12) 0%, transparent 70%)",
                                filter: "blur(16px)",
                            }}
                        />

                        {/* Image container */}
                        <div
                            className="relative w-[88px] h-[88px] flex items-center justify-center overflow-visible mb-4"
                        >
                            <div
                                className="w-full h-full flex items-center justify-center overflow-hidden"
                                style={{
                                    background: "rgba(245,245,220,0.96)",
                                    borderRadius: "10px",
                                    border: "1.5px solid rgba(122,252,32,0.22)",
                                    boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 0 0 4px rgba(122,252,32,0.05)",
                                }}
                            >
                                {imageSrc ? (
                                    <Image
                                        src={imageSrc}
                                        alt={data?.offer_title || "offer"}
                                        width={72}
                                        height={72}
                                        className="object-contain p-2"
                                    />
                                ) : (
                                    /* Fallback icon */
                                    <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="rgba(8,28,17,0.3)" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185z" />
                                    </svg>
                                )}
                            </div>

                            {/* Discount badge */}
                            {finalDiscountTag && (
                                <div
                                    className="absolute -top-2.5 -right-3 px-2 py-0.5 text-[8px] font-black uppercase tracking-widest z-10"
                                    style={{
                                        background: "#7AFC20",
                                        color: "#081C11",
                                        clipPath:
                                            "polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))",
                                        boxShadow: "0 0 12px rgba(122,252,32,0.5)",
                                    }}
                                >
                                    {finalDiscountTag}
                                </div>
                            )}
                        </div>

                        {/* Status pills */}
                        <div className="flex items-center gap-2 flex-wrap justify-center">
                            <span
                                className="flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-black uppercase tracking-widest"
                                style={{
                                    background: "rgba(122,252,32,0.07)",
                                    border: "1px solid rgba(122,252,32,0.18)",
                                    color: "#7AFC20",
                                    clipPath:
                                        "polygon(0 0, calc(100% - 4px) 0, 100% 4px, 100% 100%, 4px 100%, 0 calc(100% - 4px))",
                                }}
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{
                                        background: "#7AFC20",
                                        boxShadow: "0 0 6px #7AFC20",
                                        animation: "pulse 2s infinite",
                                    }}
                                />
                                Verified
                            </span>
                            <OfferDuration endDate={data?.end_date} />
                        </div>
                    </div>

                    {/* ── Offer title ── */}
                    <div className="px-6 pt-5 pb-4">
                        <h2
                            className="text-[15px] font-black leading-snug text-center m-0"
                            style={{ color: "#F5F5DC" }}
                        >
                            {data?.offer_title}
                        </h2>
                    </div>

                    {/* ── CTA section ── */}
                    <div className="px-6 pb-5">
                        {hasCoupon ? (
                            /* ── Coupon code block ── */
                            <div>
                                {/* Reveal / copy row */}
                                <div
                                    className="relative flex items-stretch overflow-hidden mb-2"
                                    style={{
                                        border: "1.5px dashed rgba(122,252,32,0.28)",
                                        background: "rgba(122,252,32,0.04)",
                                        clipPath:
                                            "polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))",
                                    }}
                                >
                                    {/* Code display */}
                                    <div className="flex-1 flex items-center px-4 py-3 min-w-0">
                                        {codeRevealed ? (
                                            <span
                                                className="text-sm font-black uppercase tracking-[0.22em] select-all truncate"
                                                style={{ color: "#7AFC20" }}
                                            >
                                                {data.coupon_code}
                                            </span>
                                        ) : (
                                            <span
                                                className="text-sm font-black uppercase tracking-[0.22em] select-none blur-[5px] pointer-events-none"
                                                style={{ color: "#7AFC20" }}
                                            >
                                                {data.coupon_code}
                                            </span>
                                        )}
                                    </div>

                                    {/* Divider */}
                                    <div
                                        className="w-px self-stretch"
                                        style={{ background: "rgba(122,252,32,0.15)" }}
                                    />

                                    {/* Action button */}
                                    <button
                                        onClick={() => {
                                            if (!codeRevealed) {
                                                setCodeRevealed(true);
                                            } else {
                                                handleCopy();
                                            }
                                        }}
                                        className="flex items-center gap-1.5 px-4 py-3 text-[9px] font-black uppercase tracking-widest transition-all duration-300 whitespace-nowrap focus:outline-none"
                                        style={{
                                            background: copied
                                                ? "rgba(122,252,32,0.12)"
                                                : codeRevealed
                                                ? "#7AFC20"
                                                : "rgba(122,252,32,0.1)",
                                            color: copied
                                                ? "#7AFC20"
                                                : codeRevealed
                                                ? "#081C11"
                                                : "#7AFC20",
                                        }}
                                    >
                                        {copied ? (
                                            <>
                                                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                Copied!
                                            </>
                                        ) : codeRevealed ? (
                                            <>
                                                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                                Copy
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                Reveal
                                            </>
                                        )}
                                    </button>
                                </div>

                                {/* Hint text */}
                                <p
                                    className="text-[9px] font-black uppercase tracking-[0.25em] text-center m-0 mb-4"
                                    style={{ color: "rgba(122,252,32,0.35)" }}
                                >
                                    {codeRevealed ? "Apply code at checkout" : "Click reveal to show code"}
                                </p>

                                {/* Secondary CTA — go to store */}
                                <Link
                                    href={absoluteOutUrl}
                                    target="_blank"
                                    className="group/btn relative w-full overflow-hidden flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-widest no-underline transition-all duration-300 hover:-translate-y-0.5 focus:outline-none"
                                    style={{
                                        background: "transparent",
                                        color: "#7AFC20",
                                        border: "1px solid rgba(122,252,32,0.25)",
                                        clipPath:
                                            "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))",
                                    }}
                                    onClick={handleClose}
                                >
                                    <span
                                        className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-500 skew-x-12 pointer-events-none"
                                        style={{
                                            background:
                                                "linear-gradient(90deg, transparent, rgba(122,252,32,0.08), transparent)",
                                        }}
                                    />
                                    <span className="relative z-10">Go to Store</span>
                                    <svg
                                        className="relative z-10 w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                    </svg>
                                </Link>
                            </div>
                        ) : (
                            /* ── Deal (no code) CTA ── */
                            <Link
                                href={absoluteOutUrl}
                                target="_blank"
                                className="group/btn relative w-full overflow-hidden flex items-center justify-center gap-2 py-3.5 text-[11px] font-black uppercase tracking-widest no-underline transition-all duration-300 hover:-translate-y-0.5 focus:outline-none"
                                style={{
                                    background: "#7AFC20",
                                    color: "#081C11",
                                    clipPath:
                                        "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
                                    boxShadow: "0 0 24px rgba(122,252,32,0.3), 0 4px 16px rgba(0,0,0,0.3)",
                                }}
                                onClick={handleClose}
                            >
                                <span
                                    className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-500 skew-x-12 pointer-events-none"
                                    style={{
                                        background:
                                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
                                    }}
                                />
                                <span className="relative z-10">Get Deal Now</span>
                                <svg
                                    className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                </svg>
                            </Link>
                        )}
                    </div>

                    {/* ── Description ── */}
                    {cleanDesc && (
                        <div
                            className="mx-6 mb-5 px-4 py-3.5"
                            style={{
                                background: "rgba(122,252,32,0.03)",
                                border: "1px solid rgba(122,252,32,0.08)",
                                clipPath:
                                    "polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))",
                            }}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <div
                                    className="h-px flex-1"
                                    style={{
                                        background:
                                            "linear-gradient(90deg, rgba(122,252,32,0.3), transparent)",
                                    }}
                                />
                                <span
                                    className="text-[8px] font-black uppercase tracking-[0.3em] flex-shrink-0"
                                    style={{ color: "rgba(122,252,32,0.4)" }}
                                >
                                    Offer Details
                                </span>
                                <div
                                    className="h-px flex-1"
                                    style={{
                                        background:
                                            "linear-gradient(270deg, rgba(122,252,32,0.3), transparent)",
                                    }}
                                />
                            </div>
                            <p
                                className={`text-[12px] leading-relaxed m-0 transition-all duration-300 ${
                                    !showFullDesc && isLongDesc ? "line-clamp-3" : ""
                                }`}
                                style={{ color: "rgba(245,245,220,0.5)" }}
                            >
                                {cleanDesc}
                            </p>
                            {isLongDesc && (
                                <button
                                    onClick={() => setShowFullDesc(!showFullDesc)}
                                    className="mt-2 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest transition-colors duration-200 focus:outline-none"
                                    style={{ color: "rgba(122,252,32,0.45)" }}
                                >
                                    {showFullDesc ? (
                                        <>
                                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                            </svg>
                                            Show Less
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                            </svg>
                                            Read More
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div
                    className="relative z-10 px-6 py-3.5 flex items-center justify-between gap-3"
                    style={{
                        borderTop: "1px solid rgba(122,252,32,0.07)",
                        background: "rgba(122,252,32,0.025)",
                    }}
                >
                    <div className="flex items-center gap-1.5 min-w-0">
                        <div
                            className="w-1 h-1 rounded-full flex-shrink-0"
                            style={{ background: "rgba(122,252,32,0.4)" }}
                        />
                        <Link
                            href={merchantHref}
                            className="text-[9px] font-black uppercase tracking-widest no-underline truncate transition-colors duration-200 hover:text-[#7AFC20] focus:outline-none"
                            style={{ color: "rgba(122,252,32,0.38)" }}
                            onClick={handleClose}
                        >
                            More from this store
                        </Link>
                    </div>
                    <span
                        className="text-[9px] font-black uppercase tracking-widest flex-shrink-0"
                        style={{ color: "rgba(122,252,32,0.2)" }}
                    >
                        →
                    </span>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SimpleOfferModal;
